import { type OrcamentoDetalhe } from '@/features/orcamentos/api/orcamentosApi';
import { useEtapasKanban } from '@/shared/state/EtapasKanbanContext';
import type { DadosEmpresa } from '@/features/configuracoes/api/configuracoesApi';
import type { FotoCapturaDTO } from '@/features/captura/tipos';

type Props = {
  detalhe: OrcamentoDetalhe;
  nomeEmpresa: string;
  empresa?: DadosEmpresa | null;
  termo?: string | null;
  /**
   * Fotos do veículo (captura pelo celular). Presentes e não vazias, saem
   * numa grade de 3 colunas antes dos termos. O cupom 58mm não tem fotos.
   */
  fotos?: FotoCapturaDTO[];
};

// Estilos da grade de fotos inline: valem igual na pré-visualização, na
// impressão e no PDF, sem depender da folha de estilos de outra feature.
// A seção pode quebrar entre páginas; cada foto, não.
const estiloGradeFotos: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: '6px',
};
const estiloFoto: React.CSSProperties = {
  margin: 0,
  breakInside: 'avoid',
  pageBreakInside: 'avoid',
};
const estiloImagemFoto: React.CSSProperties = {
  display: 'block',
  width: '100%',
  aspectRatio: '4 / 3',
  objectFit: 'cover',
  border: '1px solid #999',
  borderRadius: '2px',
  background: '#f3f4f6',
};
const estiloLegendaFoto: React.CSSProperties = {
  marginTop: '2px',
  fontSize: '8pt',
  color: '#333',
  lineHeight: 1.25,
};

const moeda = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const dataFmt = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

function formatarDesconto(tipo: string | null, valor: number | null): string {
  if (!tipo || valor == null || valor === 0) return '—';
  return tipo === 'Percentual' ? `${valor}%` : moeda.format(valor);
}

export function OrcamentoImpressaoA4({
  detalhe,
  nomeEmpresa,
  empresa,
  termo,
  fotos,
}: Props) {
  const { rotuloDe } = useEtapasKanban();
  // Trata "<p></p>" (saída padrão do TipTap quando vazio) também como nulo.
  const limpo = termo?.replace(/<p>\s*<\/p>/g, '').trim() ?? '';
  const termoLimpo = limpo.length > 0 ? limpo : null;
  const pecas = detalhe.itens.filter((i) => i.tipo === 'Produto');
  const servicos = detalhe.itens.filter((i) => i.tipo === 'Servico');
  // Nome da empresa: o cadastrado em Configurações tem preferência sobre o
  // do signup (passado por prop).
  const nome = empresa?.nomeEmpresa?.trim() || nomeEmpresa;
  const linhaEndereco = [
    empresa?.endereco,
    empresa?.numero,
    empresa?.bairro,
  ]
    .filter(Boolean)
    .join(', ');
  const linhaCidade = [empresa?.cidade, empresa?.estado]
    .filter(Boolean)
    .join(' - ');

  return (
    <div className="impressao-a4">
      <header className="impressao-a4__cabecalho">
        <div>
          <h1 className="impressao-a4__empresa">{nome}</h1>
          {empresa?.cnpj && (
            <div className="impressao-a4__sub">CNPJ: {empresa.cnpj}</div>
          )}
          {linhaEndereco && (
            <div className="impressao-a4__sub">{linhaEndereco}</div>
          )}
          {(linhaCidade || empresa?.cep) && (
            <div className="impressao-a4__sub">
              {[linhaCidade, empresa?.cep].filter(Boolean).join(' · ')}
            </div>
          )}
          {(empresa?.telefone || empresa?.email) && (
            <div className="impressao-a4__sub">
              {[empresa?.telefone, empresa?.email].filter(Boolean).join(' · ')}
            </div>
          )}
          <div className="impressao-a4__sub">Ordem de serviço</div>
        </div>
        <div className="impressao-a4__num">
          <div className="impressao-a4__num-rotulo">Nº</div>
          <div className="impressao-a4__num-valor">{detalhe.numero}</div>
          <div className="impressao-a4__sub">
            {rotuloDe(detalhe.status)}
          </div>
        </div>
      </header>

      <section className="impressao-a4__secao">
        <h2 className="impressao-a4__secao-titulo">Cliente</h2>
        <div className="impressao-a4__grid">
          <Campo rotulo="Nome" valor={detalhe.clienteNome} />
          <Campo rotulo="Documento" valor={detalhe.clienteDocumento ?? '—'} />
          <Campo rotulo="E-mail" valor={detalhe.clienteEmail ?? '—'} />
          <Campo rotulo="Telefone" valor={detalhe.clienteTelefone ?? '—'} />
          <Campo
            rotulo={detalhe.placa ? 'Placa' : 'Veículo'}
            valor={detalhe.veiculoDescricao}
          />
          <Campo
            rotulo="Quilometragem"
            valor={
              detalhe.kmRegistrado != null
                ? `${detalhe.kmRegistrado.toLocaleString('pt-BR')} km`
                : '—'
            }
          />
          <Campo
            rotulo="Responsável"
            valor={detalhe.colaboradorResponsavelNome ?? '—'}
          />
          <Campo rotulo="Data" valor={dataFmt.format(new Date(detalhe.data))} />
          <Campo
            rotulo="Prazo de entrega"
            valor={dataFmt.format(new Date(detalhe.prazoEntrega))}
          />
        </div>
        {detalhe.observacoes && detalhe.observacoes.trim().length > 0 && (
          <div className="impressao-a4__obs">
            <div className="impressao-a4__campo-rotulo">Observações</div>
            <div className="impressao-a4__obs-texto">{detalhe.observacoes}</div>
          </div>
        )}
      </section>

      {servicos.length > 0 && (
        <SecaoItens titulo="Serviços" itens={servicos} />
      )}
      {pecas.length > 0 && <SecaoItens titulo="Peças" itens={pecas} />}

      <section className="impressao-a4__totais">
        <div className="impressao-a4__totais-linha">
          <span>Total de serviços</span>
          <strong>{moeda.format(detalhe.totalServicos)}</strong>
        </div>
        <div className="impressao-a4__totais-linha">
          <span>Total de peças</span>
          <strong>{moeda.format(detalhe.totalPecas)}</strong>
        </div>
        <div className="impressao-a4__totais-linha">
          <span>Subtotal</span>
          <strong>{moeda.format(detalhe.subtotal)}</strong>
        </div>
        {detalhe.valorDescontoGeral > 0 && (
          <div className="impressao-a4__totais-linha">
            <span>
              Desconto geral{' '}
              {formatarDesconto(
                detalhe.descontoGeralTipo,
                detalhe.descontoGeralValor,
              )}
            </span>
            <strong>− {moeda.format(detalhe.valorDescontoGeral)}</strong>
          </div>
        )}
        <div className="impressao-a4__totais-linha impressao-a4__totais-total">
          <span>Total final</span>
          <strong>{moeda.format(detalhe.total)}</strong>
        </div>
      </section>

      {fotos && fotos.length > 0 && (
        // Sem `impressao-a4__secao`: ela evita quebra dentro da seção, e
        // uma grade grande de fotos precisa poder continuar na outra página.
        <section className="impressao-a4__fotos" style={{ marginBottom: 12 }}>
          <h2 className="impressao-a4__secao-titulo">Fotos do veículo</h2>
          <div style={estiloGradeFotos}>
            {fotos.map((f) => (
              <figure key={f.id} style={estiloFoto}>
                <img
                  src={f.url}
                  alt={f.areaRotulo}
                  loading="eager"
                  decoding="sync"
                  style={estiloImagemFoto}
                />
                <figcaption style={estiloLegendaFoto}>
                  <strong>{f.areaRotulo}</strong>
                  {f.legenda ? ` — ${f.legenda}` : ''}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {termoLimpo && (
        <section className="impressao-a4__termo">
          <h2 className="impressao-a4__secao-titulo">Termos e condições</h2>
          <div
            className="impressao-a4__termo-texto"
            dangerouslySetInnerHTML={{ __html: termoLimpo }}
          />
        </section>
      )}

      <footer className="impressao-a4__rodape">
        <div className="impressao-a4__assinatura">
          <div className="impressao-a4__linha-assinatura" />
          <div>Cliente</div>
        </div>
        <div className="impressao-a4__assinatura">
          <div className="impressao-a4__linha-assinatura" />
          <div>{nome}</div>
        </div>
      </footer>
    </div>
  );
}

function Campo({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="impressao-a4__campo">
      <div className="impressao-a4__campo-rotulo">{rotulo}</div>
      <div className="impressao-a4__campo-valor">{valor}</div>
    </div>
  );
}

function SecaoItens({
  titulo,
  itens,
}: {
  titulo: string;
  itens: OrcamentoDetalhe['itens'];
}) {
  return (
    <section className="impressao-a4__secao">
      <h2 className="impressao-a4__secao-titulo">{titulo}</h2>
      <table className="impressao-a4__tabela">
        <thead>
          <tr>
            <th>Descrição</th>
            <th>Responsável</th>
            <th className="impressao-a4__num-col">Qtd</th>
            <th className="impressao-a4__num-col">Preço unit.</th>
            <th>Desconto</th>
            <th className="impressao-a4__num-col">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {itens.map((i) => (
            <tr key={i.id}>
              <td>{i.descricao}</td>
              <td>{i.colaboradorNome ?? '—'}</td>
              <td className="impressao-a4__num-col">{i.quantidade}</td>
              <td className="impressao-a4__num-col">
                {moeda.format(i.precoUnitario)}
              </td>
              <td>{formatarDesconto(i.descontoTipo, i.descontoValor)}</td>
              <td className="impressao-a4__num-col">
                {moeda.format(i.subtotal)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
